import type { DogAgeRange } from "~/data/dogAgeRanges"
import type { DogPortraitInfo } from "~/api/auth"

const API_BASE = "/api/v1"

export interface DogProfile {
  id: number
  name: string
  sex: "male" | "female"
  age_range: DogAgeRange | null
  breed?: string | null
  mix_detail?: string | null
  archetype_key: string
  archetype_label: string | null
  archetype_icon: string | null
  assessment_session_token?: string | null
  share_celebration_seen_at?: string | null
  portrait: DogPortraitInfo
  selected_portrait_style?: string | null
  plus_locked?: boolean
  archived?: boolean
}

export interface DogsIndexResponse {
  dogs: DogProfile[]
  archived_dogs?: DogProfile[]
  active_dog_profile_id: number | null
}

async function apiErrorMessage(res: Response): Promise<string> {
  const err = await res.json().catch(() => null) as { errors?: string[] } | null
  if (err?.errors?.length) return err.errors.join(", ")
  return res.statusText || "Request failed"
}

export async function fetchDogs(): Promise<DogsIndexResponse> {
  const res = await fetch(`${API_BASE}/dogs`, {
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
  return res.json()
}

export async function fetchDog(dogId: number): Promise<{ dog: DogProfile }> {
  const res = await fetch(`${API_BASE}/dogs/${dogId}`, {
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
  return res.json()
}

export async function activateDog(dogId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/dogs/${dogId}/activate`, {
    method: "POST",
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
}

export async function reorderDogs(dogIds: number[]): Promise<DogsIndexResponse> {
  const res = await fetch(`${API_BASE}/dogs/reorder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dog_ids: dogIds }),
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
  return res.json()
}

export async function archiveDog(dogId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/dogs/${dogId}/archive`, {
    method: "POST",
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
}

export async function restoreDog(dogId: number): Promise<{ dog: DogProfile }> {
  const res = await fetch(`${API_BASE}/dogs/${dogId}/restore`, {
    method: "POST",
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
  return res.json()
}

export async function deleteDog(dogId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/dogs/${dogId}`, {
    method: "DELETE",
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
}

export async function chooseFreeActiveDog(dogId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/dogs/${dogId}/choose_free_active`, {
    method: "POST",
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
}

export async function updateDog(
  dogId: number,
  updates: { name?: string; breed?: string | null; mix_detail?: string | null; selected_portrait_style?: string }
): Promise<{ dog: DogProfile }> {
  const res = await fetch(`${API_BASE}/dogs/${dogId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dog: updates }),
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
  return res.json()
}
